/**
 * Notice showing hidden search results that were excluded by status filter
 * Story 278: Show hidden search results with expand option
 *
 * Appears when search query matches stories that are hidden by the current
 * status filter (e.g., searching for a done story with default filter).
 */

import { useState, useCallback } from 'react';
import { useStories, type StoryFilters, type StoryListItem } from '../../hooks/useStories';
import { StoryNode } from './StoryNode';
import './HiddenResultsNotice.css';

/** All possible story statuses */
const ALL_STATUSES = ['proposed', 'in-progress', 'blocked', 'paused', 'done'];

interface HiddenResultsNoticeProps {
  hiddenCount: number;
  searchQuery: string;
  statusFilter: string[];
  projectSlug: string;
  selectedStoryId: string | null;
  onSelectStory: (storyExternalId: string) => void;
}

export function HiddenResultsNotice({
  hiddenCount,
  searchQuery,
  statusFilter,
  projectSlug,
  selectedStoryId,
  onSelectStory,
}: HiddenResultsNoticeProps) {
  const [expanded, setExpanded] = useState(false);

  // Calculate inverted status filter (statuses NOT in current filter)
  const excludedStatuses = ALL_STATUSES.filter(s => !statusFilter.includes(s));

  // Build filters for hidden stories query
  const hiddenFilters: StoryFilters = {
    status: excludedStatuses,
    search: searchQuery,
  };

  // Only fetch when expanded
  const { data: hiddenStoriesData, isLoading } = useStories(
    projectSlug,
    hiddenFilters
  );
  const hiddenStories = expanded ? (hiddenStoriesData?.stories ?? []) : [];

  const handleToggle = useCallback(() => {
    setExpanded(prev => !prev);
  }, []);

  // Don't render if no search query or no hidden items
  if (!searchQuery.trim() || hiddenCount === 0) {
    return null;
  }

  // Singular/plural grammar
  const itemText = hiddenCount === 1 ? 'item is' : 'items are';

  return (
    <div className="hidden-results-notice">
      <hr className="hidden-results-notice__divider" />
      <p className="hidden-results-notice__message">
        <em>
          {hiddenCount} additional {itemText} excluded due to their state being
          hidden by the filter
        </em>
        <button
          type="button"
          className="hidden-results-notice__toggle"
          onClick={handleToggle}
        >
          {expanded ? 'Hide' : 'Show'}
        </button>
      </p>

      {expanded && (
        <div className="hidden-results-notice__items">
          {isLoading ? (
            <div className="hidden-results-notice__loading">Loading...</div>
          ) : (
            hiddenStories.map((story: StoryListItem) => (
              <div key={story.id} className="hidden-results-notice__item">
                <StoryNode
                  story={story}
                  isSelected={selectedStoryId === story.external_id}
                  onSelect={() => onSelectStory(story.external_id)}
                />
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
